import { Node, mergeAttributes } from "@tiptap/core";
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer, ReactNodeViewProps } from "@tiptap/react";
import { DiffButtons } from "../ui/diffButtons";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    diffInline: {
      unwrapDiffInline: (options: { pos: number; nodeSize: number }) => ReturnType;
      removeDiffInline: (options: { pos: number; nodeSize: number }) => ReturnType;
      acceptDiffInline: (options: { pos: number; nodeSize: number; diffType: string }) => ReturnType;
      rejectDiffInline: (options: { pos: number; nodeSize: number; diffType: string }) => ReturnType;
    };
  }
}

function DiffInlineComponent({ node, getPos, editor }: ReactNodeViewProps) {
  const diffType = node.attrs.diffType as "add" | "del" | "equal";

  const accept = () => {
    const pos = getPos();
    if (pos === undefined) return;
    editor.commands.acceptDiffInline({ pos, nodeSize: node.nodeSize, diffType });
  };

  const discard = () => {
    const pos = getPos();
    if (pos === undefined) return;
    editor.commands.rejectDiffInline({ pos, nodeSize: node.nodeSize, diffType });
  };

  if (diffType === "equal") {
    return (
      <NodeViewWrapper as="span" className="diff-inline">
        <NodeViewContent contentEditable={true} />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      as="span"
      data-diff-type={diffType}
      className="diff-inline group"
      style={{
        backgroundColor: diffType === "del" ? "#fee2e2" : "#dcfce7",
        color: diffType === "del" ? "#7f1d1d" : "#166534",
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        padding: "1px 2px",
        borderRadius: "2px",
        margin: "0 1px"
      }}
    >
      <NodeViewContent contentEditable={diffType !== "del"} style={{ display: "inline" }} />
      <DiffButtons accept={accept} discard={discard} type={diffType} />
    </NodeViewWrapper>
  );
}

export const DiffInline = Node.create({
  name: "diffInline",
  inline: true,
  group: "inline",
  content: "inline*",
  marks: "_", // allow any marks

  addAttributes() {
    return {
      diffType: {
        default: "add",
        parseHTML: (el) => (el.getAttribute("data-diff-type") as "add" | "del") ?? "add",
        renderHTML: (attrs) => ({ "data-diff-type": attrs.diffType })
      }
    };
  },

  parseHTML() {
    return [{ tag: "span[data-diff-type]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { class: "diff-inline" }), 0];
  },

  addCommands() {
    return {
      unwrapDiffInline:
        (options) =>
        ({ tr, state, dispatch }) => {
          if (dispatch) {
            const { pos, nodeSize } = options;
            const node = state.doc.nodeAt(pos);
            if (node && pos + nodeSize <= state.doc.content.size) {
              tr.replaceWith(pos, pos + nodeSize, node.content);
            }
          }
          return true;
        },

      removeDiffInline:
        (options) =>
        ({ tr, state, dispatch }) => {
          if (dispatch) {
            const { pos, nodeSize } = options;
            if (pos + nodeSize <= state.doc.content.size) {
              tr.delete(pos, pos + nodeSize);
            }
          }
          return true;
        },

      acceptDiffInline:
        (options) =>
        ({ commands }) => {
          const { pos, nodeSize, diffType } = options;
          if (diffType === "del") {
            // Accept deletion: remove the entire node
            return commands.removeDiffInline({ pos, nodeSize });
          } else {
            // Accept addition/equal: unwrap (keep content, remove wrapper)
            return commands.unwrapDiffInline({ pos, nodeSize });
          }
        },

      rejectDiffInline:
        (options) =>
        ({ commands }) => {
          const { pos, nodeSize, diffType } = options;
          if (diffType === "add") {
            // Reject addition: remove the entire node
            return commands.removeDiffInline({ pos, nodeSize });
          } else {
            // Reject deletion/equal: unwrap (keep content, remove wrapper)
            return commands.unwrapDiffInline({ pos, nodeSize });
          }
        }
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(DiffInlineComponent);
  }
});
